import React, { useEffect, useId, useState } from "react";
import { EventApi } from "@fullcalendar/core";
import { Calendar, Lock } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/lib/shadcn/ui/dialog";
import { Button } from "@/lib/shadcn/ui/button";
import { Input } from "@/lib/shadcn//ui/input";
import { Label } from "@/lib/shadcn//ui/label";
import { Textarea } from "@/lib/shadcn/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/lib/shadcn/ui/select";
import { reasonLabel } from "@/utils/blocks";

interface CalendarEventDialogProps {
    open: boolean; // Changed from show
    onClose: () => void; // Changed from handleClose
    onDelete: () => void; // Changed from handleDelete
    onUpdate: (id: string, apartmentId: number, start: string, end: string, notes: string) => void;
    onAddNew: (apartmentId: number, start: string, end: string, notes: string) => void;
    dateStart: string;
    dateEnd: string;
    existingEvent: EventApi | null;
    apartmentOptions: { label: string; value: string }[];
    /**
     * When true the dialog is a read-only view of a HostHub-sourced block:
     * Save and Delete are hidden, the form fields are disabled, and a muted
     * "Managed by HostHub" note + the block reason are shown. Mutations are
     * unreachable — the block is edited in HostHub, not here. Manual blocks
     * leave this undefined/false and keep the full add/edit/delete dialog.
     */
    readOnly?: boolean;
}

const CalendarEventDialog: React.FC<CalendarEventDialogProps> = ({
    open,
    onClose,
    onDelete,
    onUpdate,
    onAddNew,
    dateStart,
    dateEnd,
    apartmentOptions,
    existingEvent,
    readOnly = false,
}) => {
    const [start, setStart] = useState(dateStart);
    const [end, setEnd] = useState(dateEnd);
    const [notes, setNotes] = useState("");
    const [apartmentId, setApartmentId] = useState("1");
    const [currentEvent, setCurrentEvent] = useState<EventApi | null>(existingEvent);

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (readOnly) {
            return;
        }
        if (currentEvent) {
            onUpdate(currentEvent.id, parseInt(apartmentId), start, end, notes);
            return;
        }
        onAddNew(parseInt(apartmentId), start, end, notes);
    };

    const handleClose = () => {
        setCurrentEvent(null);
        onClose();
    };

    useEffect(() => {
        setCurrentEvent(existingEvent);
    }, [existingEvent]);

    useEffect(() => {
        if (currentEvent) {
            setApartmentId(currentEvent.extendedProps.apartmentId);
            setNotes(currentEvent.extendedProps.notes);
            setStart(currentEvent.startStr);
            setEnd(currentEvent.endStr);
        } else {
            setApartmentId("0");
            setStart(start);
            setEnd(end);
            setNotes("");
        }
    }, [currentEvent]);

    useEffect(() => {
        setStart(dateStart);
        setEnd(dateEnd);
    }, [dateStart, dateEnd]);

    // The HostHub block reason ("Owner's visit", "Housekeeping", ...) — only
    // present on hosthub-origin events; surfaced in read-only mode so the
    // distinction is text-bearing, never colour-only.
    const reason = (currentEvent?.extendedProps?.reason as string | undefined) ?? "";

    // Stable per-instance ids so each Label is programmatically tied to its
    // control (WCAG 1.3.1) — without this, screen readers don't announce the
    // field name, which is especially bad in read-only mode (disabled fields).
    const fieldId = useId();

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent data-testid="calendar-event-modal" className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>Event Details</DialogTitle>
                    <DialogDescription>
                        {readOnly
                            ? "Read-only details for a block managed by HostHub."
                            : "Add, edit or remove a manual blocked period."}
                    </DialogDescription>
                </DialogHeader>

                {readOnly && (
                    <div
                        data-testid="managed-by-hosthub-note"
                        role="note"
                        className="flex items-start gap-2 rounded-md bg-muted/60 p-3 text-sm text-muted-foreground"
                    >
                        <Lock aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
                        <span>
                            Managed by HostHub — edit this block in HostHub.
                            {reason ? <span className="block font-medium text-foreground">{reasonLabel(reason)}</span> : null}
                        </span>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor={`${fieldId}-apartment`}>Apartment</Label>
                            <Select value={apartmentId} onValueChange={setApartmentId} disabled={readOnly}>
                                <SelectTrigger id={`${fieldId}-apartment`}>
                                    <SelectValue placeholder="Select apartment" />
                                </SelectTrigger>
                                <SelectContent>
                                    {apartmentOptions.map((option) => (
                                        <SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor={`${fieldId}-start`}>Select start date</Label>
                            <div className="relative">
                                <Calendar aria-hidden="true" className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                                <Input
                                    id={`${fieldId}-start`}
                                    type="date"
                                    required
                                    value={start}
                                    onChange={(e) => setStart(e.target.value)}
                                    className="pl-10"
                                    disabled={readOnly}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor={`${fieldId}-end`}>Select end date</Label>
                            <div className="relative">
                                <Calendar aria-hidden="true" className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                                <Input
                                    id={`${fieldId}-end`}
                                    type="date"
                                    required
                                    value={end}
                                    onChange={(e) => setEnd(e.target.value)}
                                    className="pl-10"
                                    disabled={readOnly}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor={`${fieldId}-notes`}>Notes</Label>
                            <Textarea
                                id={`${fieldId}-notes`}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                rows={4}
                                disabled={readOnly}
                            />
                        </div>
                    </div>

                    <DialogFooter className="gap-2">
                        {!readOnly && currentEvent && (
                            <Button type="button" variant="destructive" onClick={onDelete}>
                                Delete event
                            </Button>
                        )}
                        <div className="flex gap-2">
                            <Button type="button" variant="outline" onClick={handleClose}>
                                Close
                            </Button>
                            {!readOnly && (
                                <Button type="submit">{existingEvent ? "Update event" : "Add event"}</Button>
                            )}
                        </div>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default CalendarEventDialog;
